import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { cn } from "@/lib/utils";
import type { PickRef, PickRefPageContext } from "@/lib/brand-site-pick-refs";
import {
  applyComposerToEditorRoot,
  insertPickAtSelection,
  normalizePickRefEditorRoot,
  parsePickRefComposerFromClipboard,
  parsePickRefEditorRoot,
  type PickRefComposerContent,
} from "@/lib/pick-ref-mixed-editor";
import { emitBrandSitePickHover } from "@/lib/brand-site-preview-nav";

export type PickRefMixedEditorHandle = {
  insertPick: (pick: PickRef) => void;
  getContent: () => PickRefComposerContent | null;
  clear: () => void;
  focus: () => void;
  applyContent: (content: PickRefComposerContent) => void;
};

type PickRefMixedEditorProps = {
  pageContext: PickRefPageContext;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** 嵌入 Hermes InputGroup 时与 textarea 同槽位，避免多余包裹导致布局错位 */
  variant?: "default" | "input-group";
  className?: string;
  onChange?: (content: PickRefComposerContent | null) => void;
  onSubmit?: () => void;
};

/** 点选芯片与文字混排输入（contenteditable）；支持复制粘贴保留芯片顺序。 */
export const PickRefMixedEditor = forwardRef<PickRefMixedEditorHandle, PickRefMixedEditorProps>(
  function PickRefMixedEditor(
    {
      pageContext,
      placeholder = "描述要如何改这些选区…",
      autoFocus,
      disabled,
      variant = "default",
      className,
      onChange,
      onSubmit,
    },
    ref,
  ) {
    const rootRef = useRef<HTMLDivElement>(null);

    const syncContext = useCallback(() => {
      const root = rootRef.current;
      if (!root) return;
      root.dataset.pageSlug = pageContext.pageSlug;
      root.dataset.pagePath = pageContext.pagePath;
      root.dataset.pageTitle = pageContext.pageTitle ?? "";
    }, [pageContext.pagePath, pageContext.pageSlug, pageContext.pageTitle]);

    const emitChange = useCallback(() => {
      const root = rootRef.current;
      if (!root || !onChange) return;
      onChange(parsePickRefEditorRoot(root));
    }, [onChange]);

    useImperativeHandle(
      ref,
      () => ({
        insertPick(pick: PickRef) {
          const root = rootRef.current;
          if (!root) return;
          syncContext();
          insertPickAtSelection(root, pick);
          normalizePickRefEditorRoot(root);
          emitChange();
        },
        getContent() {
          const root = rootRef.current;
          if (!root) return null;
          syncContext();
          return parsePickRefEditorRoot(root);
        },
        clear() {
          const root = rootRef.current;
          if (!root) return;
          root.innerHTML = "";
          emitChange();
        },
        focus() {
          rootRef.current?.focus();
        },
        applyContent(content: PickRefComposerContent) {
          const root = rootRef.current;
          if (!root) return;
          applyComposerToEditorRoot(root, content);
          emitChange();
        },
      }),
      [emitChange, syncContext],
    );

    useEffect(() => {
      syncContext();
    }, [syncContext]);

    useEffect(() => {
      if (!autoFocus) return;
      rootRef.current?.focus();
    }, [autoFocus]);

    useEffect(() => {
      return () => emitBrandSitePickHover(null);
    }, []);

    function handleInput() {
      const root = rootRef.current;
      if (root) normalizePickRefEditorRoot(root);
      emitChange();
    }

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        onSubmit?.();
      }
    }

    function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
      const text = event.clipboardData.getData("text/plain");
      const html = event.clipboardData.getData("text/html");
      const parsed = parsePickRefComposerFromClipboard(text, html);
      const root = rootRef.current;
      if (!root) return;

      if (parsed) {
        event.preventDefault();
        syncContext();
        if (parsed.pageSlug) {
          root.dataset.pageSlug = parsed.pageSlug;
          root.dataset.pagePath = parsed.pagePath;
          root.dataset.pageTitle = parsed.pageTitle ?? "";
        }
        const current = parsePickRefEditorRoot(root);
        if (!current || current.segments.length === 0) {
          applyComposerToEditorRoot(root, parsed);
        } else {
          for (const segment of parsed.segments) {
            if (segment.type === "text") {
              root.appendChild(document.createTextNode(segment.text));
            } else {
              insertPickAtSelection(root, segment.pick);
            }
          }
          normalizePickRefEditorRoot(root);
        }
        emitChange();
        return;
      }

      // 浏览器原生粘贴 HTML 芯片时，下一帧再同步状态
      requestAnimationFrame(() => {
        if (!rootRef.current) return;
        normalizePickRefEditorRoot(rootRef.current);
        emitChange();
      });
    }

    function handleChipHover(event: ReactMouseEvent<HTMLDivElement>) {
      const chip = (event.target as HTMLElement | null)?.closest?.(
        "[data-pick-ref]",
      ) as HTMLElement | null;
      if (!chip || !rootRef.current?.contains(chip)) return;
      const path = chip.getAttribute("data-pick-path");
      if (path) emitBrandSitePickHover(path);
    }

    function handleChipUnhover(event: ReactMouseEvent<HTMLDivElement>) {
      const chip = (event.target as HTMLElement | null)?.closest?.(
        "[data-pick-ref]",
      ) as HTMLElement | null;
      if (!chip || !rootRef.current?.contains(chip)) return;
      const related = event.relatedTarget as Node | null;
      if (related && chip.contains(related)) return;
      emitBrandSitePickHover(null);
    }

    const editorClassName = cn(
      variant === "input-group"
        ? "field-sizing-content flex-1 resize-none rounded-none border-0 bg-transparent px-3 py-3 shadow-none ring-0 outline-none focus-visible:ring-0"
        : "px-0.5 py-0.5 outline-none",
      "max-h-[7.5rem] w-full overflow-y-auto whitespace-pre-wrap break-words text-sm leading-normal text-foreground",
      variant === "default" && "min-h-[2.75rem]",
      variant === "input-group" && "min-h-12",
      "empty:before:pointer-events-none empty:before:text-foreground/45 empty:before:content-[attr(data-placeholder)]",
      disabled && "cursor-not-allowed opacity-60",
      className,
    );

    const editor = (
      <div
        ref={rootRef}
        role="textbox"
        aria-multiline="true"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        data-pick-composer-input={variant === "input-group" ? "" : undefined}
        data-slot={variant === "input-group" ? "input-group-control" : undefined}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onMouseOver={handleChipHover}
        onMouseOut={handleChipUnhover}
        className={editorClassName}
      />
    );

    if (variant === "input-group") {
      return editor;
    }

    return <div className={cn("relative min-w-0 flex-1", className)}>{editor}</div>;
  },
);
